import { useState } from "react";
import { authClient } from "./auth.js";
import { useCreateWorkspace } from "./ui/workspace.js";
import { Logo } from "./ui/logo.js";

/**
 * Signed in, with no open workspace to land in ([ADR 0022](../../../docs/adr/0022-closing-a-workspace-locks-it-at-once-and-leaves-a-tombstone.md))
 * — somebody who closed their last one. Not an error and not an empty app: every workspace route answers `no_workspace`,
 * so there is nothing to draw but the way forward, a new workspace, and the way out.
 *
 * The credit is still theirs (ADR 0021), and that is worth saying: it is the one thing a person might think went with it.
 */
export function NoWorkspace({ email }: { email: string }) {
  const make = useCreateWorkspace();
  const [name, setName] = useState("");
  return (
    <div className="h-full grid place-items-center bg-page p-6">
      <div className="w-full max-w-[440px] bg-panel border border-line rounded-2xl p-6 flex flex-col gap-4 shadow-[0_14px_40px_rgba(23,23,26,.08)]">
        <Logo size={30} />
        <div>
          <h1 className="text-[18px] font-semibold">You have no open workspace</h1>
          <p className="text-[13px] text-muted mt-1.5 leading-relaxed">
            You are signed in as {email}. A workspace that has been closed cannot be opened again, and what was in it is
            deleted thirty days after it closed. Your credit is still yours: a new workspace spends from the same balance.
          </p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) make.mutate(name.trim()); }} className="flex items-center gap-2">
          <input
            autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
            placeholder="The business's name" aria-label="Name of the new workspace"
            className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-line-2 bg-page text-[13.5px]"
          />
          <button type="submit" disabled={!name.trim() || make.isPending} className="shrink-0 px-3.5 py-2 rounded-lg bg-ink text-white text-[13px] font-medium disabled:opacity-60">Create</button>
        </form>
        {make.error ? <p role="alert" className="text-[12px] text-bad">{String(make.error)}</p> : null}
        <button onClick={() => authClient.signOut().then(() => location.reload())} className="self-start text-[13px] text-muted hover:text-ink">
          Sign out
        </button>
      </div>
    </div>
  );
}
